import { Component, OnInit, OnDestroy, Inject, ViewChild } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { RequestService, LayoutUtilsService, LoaderService } from "src/app/shared";
import { SignalingService } from "src/app/shared/services/signaling.service";

interface DialogData {
    caseId: string;
    type: string | string[];
    ai_flag?: string;
    add_note?: boolean;
    metadata: any;
    group?: string;
}

enum FileTypes {
    VIDEO = '.mp4, .wmv, .mov',
    AUDIO = '.mp3, .wav, .m4a',
    IMAGE = '.png, .jpeg, .jpg',
    FILE = '.pdf, .doc, .docx, .xlsx',
    MEDIA = '.mp4, .wmv, .mov,.mp3, .wav, .m4a,.png, .jpeg, .jpg, .pdf, .doc, .docx, .xlsx'
}

enum MaxFileSize {
    VIDEO = 40,
    AUDIO = 40,
    IMAGE = 40,
    FILE = 40
}

enum TypesEnum {
    VIDEO = 'Video',
    AUDIO = 'Audio',
    IMAGE = 'Image',
    FILE = 'Document',
    MEDIA = 'Media',
};

@Component({
    selector: 'app-document-upload-dialog',
    templateUrl: './document-upload-dialog.component.html',
    styleUrls: ['./document-upload-dialog.component.scss']
})
export class DocumentUploadDialogComponent implements OnInit, OnDestroy {

    public accept: string = undefined;
    public selectedFileName: string = undefined;
    public titleDisplayName: string = undefined;
    public form: FormGroup;
    public titleMetadata: any = {};
    public descriptionLimit: number = 400;
    // public selectedType: string = undefined;

    public selectedFiles: File[] = [];
    uploadMessages: { message: string; status: boolean }[] = [];

    fileFormList: { file: File; title: string }[] = [];
    uploadProgress: number[] = [];
    public uploading: boolean = false;
    public uploadedOnce: boolean = false;

    constructor(public dialogRef: MatDialogRef<DocumentUploadDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: DialogData, private requestService: RequestService, private translate: TranslateService, private layoutUtilsService: LayoutUtilsService, private loader: LoaderService, private formBuilder: FormBuilder, private signalingService: SignalingService) { }

    /** Normalize data.type to an array (single string becomes [string]). */
    private getTypesArray(): string[] {
        const t = this.data.type;
        if (Array.isArray(t)) return t.length ? t : [];
        return t ? [t] : [];
    }

    /** Return true when data.type is a single value equal to 'Media' (case-insensitive). */
    private isMediaType(): boolean {
        const types = this.getTypesArray();
        return types.length === 1 && (types[0] || '').toLowerCase() === 'media';
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            files: this.formBuilder.array([])
        });

        const types = this.getTypesArray();
        const typeKeys = types.map(t => (t || '').toUpperCase());
        const acceptKeys = typeKeys
            .map(k => (k === 'DOCUMENT' ? 'FILE' : k))
            .filter(k => !!FileTypes[k]);

        if (acceptKeys.length === 0) {
            this.accept = FileTypes.MEDIA;
            this.titleDisplayName = this.translate.instant(TypesEnum.MEDIA);
        } else if (acceptKeys.length === 1) {
            const acceptKey = acceptKeys[0];
            this.accept = FileTypes[acceptKey] || FileTypes.MEDIA;
            if (!this.isMediaType()) {
                this.titleDisplayName = this.translate.instant(TypesEnum[acceptKey] || TypesEnum.MEDIA);
            }
        } else {
            const extensions = new Set<string>();
            acceptKeys.forEach(key => {
                const ft = FileTypes[key];
                if (ft) ft.split(',').map(ext => ext.trim()).forEach(ext => extensions.add(ext));
            });
            this.accept = Array.from(extensions).join(', ') || FileTypes.MEDIA;
            this.titleDisplayName = acceptKeys
                .map(k => this.translate.instant(TypesEnum[k] || TypesEnum.MEDIA))
                .join(', ');
        }

        const fields = this.data.metadata?.fields;
        this.titleMetadata = Array.isArray(fields) ? fields.find(i => i.name == 'name') : undefined;
        // if (!this.titleMetadata.nullable) {
        //     this.form.controls['title'].setValidators([Validators.required]);
        // }
    }

    ngOnDestroy(): void {

    }
    get filesArray(): FormArray {
        return this.form?.get('files') as FormArray;
    }
    // onFileSelected(event: any) {
    //     this.selectedFile = event.target.files[0];
    //     if (this.selectedFile) {
    //         const extension = this.selectedFile.name.split('.').pop()?.toLowerCase();
    //         this.selectedType = this.getFileTypeByExtension(extension);
    //         this.selectedFileName = this.selectedFile.name;
    //     }
    //     else {
    //         this.selectedFileName = undefined;
    //     }
    // }
    onFileSelected(event: any) {
        const files = event.target.files;
        this.uploadMessages = [];
        // this.filesArray.clear(); // Clear previous files

        this.uploading = false;
        if (files && files.length > 0) {
            Array.from(files).forEach((file: File) => {
                this.filesArray.push(this.formBuilder.group({
                    file: [file],
                    fileName: [file.name],
                    title: ['', Validators.required],
                    description: ['']
                }));
            });
        }
    }

    // selectedTypeChange(value) {
    //     this.selectedType = value;
    //     this.accept = FileTypes[this.selectedType.toUpperCase()];
    //     this.titleDisplayName = this.translate.instant(TypesEnum[this.selectedType.toUpperCase()]);
    // }
    getFileTypeByExtension(extension: string): string | null {
        const normalizedExt = extension.startsWith('.') ? extension.toLowerCase() : '.' + extension.toLowerCase();

        for (const [key, value] of Object.entries(FileTypes)) {
            const extensions = value.split(',').map(ext => ext.trim());
            if (extensions.includes(normalizedExt)) {
                return key.toLowerCase(); // return in lowercase
            }
        }

        return null; // unknown type
    }
    uploadFiles() {
        if (this.filesArray.invalid) return;
        this.uploading = true;
        this.uploadedOnce = true;
        this.loader.display(true);
        this.uploadMessages = Array(this.filesArray.length).fill(null);

        // Check if we need sequential uploads (add_note is true and multiple files)
        const needsSequentialUpload = this.data.add_note && this.filesArray.length > 1;

        if (needsSequentialUpload) {
            this.uploadFilesSequentially();
        } else {
            this.uploadFilesInParallel();
        }
    }

    private uploadFilesInParallel(): void {
        let completed = 0;
        let hasError = false;

        this.filesArray.controls.forEach((control: FormGroup, index: number) => {
            this.uploadSingleFile(control, index, (success: boolean) => {
                if (!success) hasError = true;
                completed++;
                this.checkAllUploadsCompleted(completed, hasError);
            });
        });
    }

    private uploadFilesSequentially(): void {
        let noteId: string = null;
        let currentIndex = 0;
        let hasError = false;

        const uploadNext = () => {
            if (currentIndex >= this.filesArray.length) {
                this.checkAllUploadsCompleted(this.filesArray.length, hasError);
                return;
            }

            const control = this.filesArray.controls[currentIndex] as FormGroup;
            const isFirstFile = currentIndex === 0;

            this.uploadSingleFile(control, currentIndex, (success: boolean, response?: any) => {
                if (!success) {
                    hasError = true;
                } else if (isFirstFile && response?.results?.note?._id) {
                    // Get the note _id from the first upload response
                    noteId = response.results.note._id;
                }

                currentIndex++;
                uploadNext();
            }, noteId);
        };

        uploadNext();
    }
    signalMaterialUpdate() {
        this.signalingService.publish(`case-${this.data.caseId}`, {
            type: 'material-updated',
            caseId: this.data.caseId,
            action: 'upload'
        }, this.signalingService.materialTopicName);
    }
    private uploadSingleFile(
        control: FormGroup,
        index: number,
        callback: (success: boolean, response?: any) => void,
        noteId?: string
    ): void {
        const file: File = control.get('file')?.value;
        const title: string = control.get('title')?.value;
        const description: string = control.get('description')?.value;
        const extension = file.name.split('.').pop()?.toLowerCase();

        // Extension validation
        if (!extension || this.accept.indexOf(`.${extension}`) === -1) {
            this.uploadMessages[index] = {
                message: this.translate.instant('Invalid file type'),
                status: false
            };
            callback(false);
            return;
        }

        const selectedType = this.getFileTypeByExtension(extension);
        const types = this.getTypesArray();
        const allowedTypeKeys = types.map(t => (t || '').toUpperCase()).map(k => (k === 'DOCUMENT' ? 'FILE' : k));
        const isMultiType = allowedTypeKeys.length > 1;

        if (isMultiType) {
            const selectedKey = selectedType ? (selectedType === 'file' ? 'FILE' : selectedType.toUpperCase()) : null;
            if (!selectedKey || !allowedTypeKeys.includes(selectedKey)) {
                this.uploadMessages[index] = {
                    message: this.translate.instant('Invalid file type'),
                    status: false
                };
                callback(false);
                return;
            }
        }

        const limitKey = isMultiType && selectedType
            ? (selectedType === 'file' ? 'FILE' : selectedType.toUpperCase())
            : (allowedTypeKeys[0] || '');
        const allowedSize = this.isMediaType() || isMultiType
            ? (selectedType ? MaxFileSize[selectedType === 'file' ? 'FILE' : selectedType.toUpperCase()] : undefined)
            : MaxFileSize[limitKey];

        if (!allowedSize) {
            this.uploadMessages[index] = {
                message: this.translate.instant('Invalid file type'),
                status: false
            };
            callback(false);
            return;
        }

        // Size validation
        if (file.size > allowedSize * 1024 * 1024) {
            this.uploadMessages[index] = {
                message: `${this.translate.instant('File size exceeds')} ${allowedSize}MB`,
                status: false
            };
            callback(false);
            return;
        }

        const fileData = {
            originalFile: file,
            text: file.name
        };

        const note = this.data.ai_flag ? { ai_flag: this.data.ai_flag } : {};
        if (this.data.add_note) {
            note['name'] = this.translate.instant("Material added")
            note['type'] = "material"
            // If noteId is provided (from first upload), use it for subsequent uploads
            if (noteId) {
                note['_id'] = noteId;
            }
        }

        const uploadType = this.isMediaType() || isMultiType ? selectedType : (Array.isArray(this.data.type) ? this.data.type[0] : this.data.type);
        this.requestService.onUploadFile(
            fileData,
            this.data.caseId,
            uploadType,
            title,
            note,
            description,
            this.data.group || undefined
        ).subscribe(
            (results: any) => {
                this.uploadMessages[index] = {
                    message: results.status
                        ? this.translate.instant('Upload successful')
                        : this.translate.instant('Upload failed'),
                    status: results.status
                };

                callback(results.status, results);
            },
            () => {
                this.uploadMessages[index] = {
                    message: this.translate.instant('Upload failed'),
                    status: false
                };

                callback(false);
            }
        );
    }

    private checkAllUploadsCompleted(completed: number, hasError: boolean): void {
        if (completed === this.filesArray.length) {
            this.loader.display(false);

            // Only close the dialog if all succeeded
            if (!hasError) {
                this.signalMaterialUpdate();
                this.dialogRef.close(true);
            }
        }
    }
    removeFile(index: number): void {
        this.filesArray.removeAt(index);
        this.uploadMessages.splice(index, 1);
    }
    close() {
        this.dialogRef.close(this.uploadedOnce);
    }
}
